import React from 'react';
import { X, Calculator, User, Building2, Target, TrendingUp, Award, Users, DollarSign, Clock } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const ROITypeSelector = ({ onClose, onSelectType }) => {
  const { t } = useTranslation();

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden" data-testid="roi-calculator-modal">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-5 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <Calculator className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-xl font-bold">{t('roiCalculator.title')}</h2>
                <p className="text-blue-100 text-sm">{t('roiCalculator.subtitle')}</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg transition-colors" data-testid="roi-calculator-close">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Type Selection */}
        <div className="p-6">
          <p className="text-gray-600 text-center mb-6">
            {t('roiCalculator.selectType')}
          </p>
          
          <div className="grid md:grid-cols-2 gap-4">
            {/* Individual Option */}
            <button
              onClick={() => onSelectType('individual')}
              className="p-6 border-2 border-gray-200 rounded-xl hover:border-emerald-500 hover:bg-emerald-50 transition-all text-left group"
              data-testid="roi-type-individual"
            >
              <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-emerald-200 transition-colors">
                <User className="w-6 h-6 text-emerald-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {t('roiCalculator.individual.title')}
              </h3>
              <p className="text-sm text-gray-600">
                {t('roiCalculator.individual.description')}
              </p>
              <ul className="mt-4 space-y-2 text-sm text-gray-500">
                <li className="flex items-center gap-2">
                  <Target className="w-4 h-4 text-emerald-500" />
                  {t('roiCalculator.individual.goals.promotion')}
                </li>
                <li className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-emerald-500" />
                  {t('roiCalculator.breakdown.salaryIncrease')}
                </li>
                <li className="flex items-center gap-2">
                  <Award className="w-4 h-4 text-emerald-500" />
                  {t('roiCalculator.breakdown.skillsDevelopment')}
                </li>
              </ul>
            </button>

            {/* Corporate Option */}
            <button
              onClick={() => onSelectType('corporate')}
              className="p-6 border-2 border-gray-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-all text-left group"
              data-testid="roi-type-corporate"
            >
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-blue-200 transition-colors">
                <Building2 className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {t('roiCalculator.corporate.title')}
              </h3>
              <p className="text-sm text-gray-600">
                {t('roiCalculator.corporate.description')}
              </p>
              <ul className="mt-4 space-y-2 text-sm text-gray-500">
                <li className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-blue-500" />
                  {t('roiCalculator.breakdown.productivityGain')}
                </li>
                <li className="flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-blue-500" />
                  {t('roiCalculator.breakdown.turnoverReduction')}
                </li>
                <li className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-blue-500" />
                  {t('roiCalculator.breakdown.revenueImpact')}
                </li>
              </ul>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ROITypeSelector;
